import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth, applyActionCode, confirmPasswordReset, checkActionCode } from 'firebase/auth';
import { firebaseErrorHandler } from '../service/firebase';
import {
  Box,
  Typography,
  OutlinedInput,
  Button,
  CircularProgress,
  Container,
  Paper,
  Alert,
  useTheme,
  useMediaQuery,
  Snackbar,
  InputAdornment,
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { analytics } from '../helper/helper';

const AuthAction = () => {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState('');
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const actionMode = searchParams.get('mode');
    const actionCode = searchParams.get('oobCode');

    if (!actionCode) {
      setError('Invalid action code');
      setStatus('error');
      return;
    }

    if (!actionMode) {
      setError('Invalid mode');
      setStatus('error');
      return;
    }

    setMode(actionMode);

    if (actionMode === 'verifyEmail') {
      handleEmailVerification(actionCode);
    } else if (actionMode === 'resetPassword') {
      setStatus('resetPassword');
    } else {
      setError('Invalid action');
      setStatus('error');
    }
  }, [searchParams]);

  const handleEmailVerification = async (actionCode) => {
    try {
      await checkActionCode(auth, actionCode);
      await applyActionCode(auth, actionCode);
      setStatus('success');
      setShowSuccessMessage(true);
      analytics.track('Email Verification Success');
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setError(firebaseErrorHandler(typeof error.code === 'string' ? error.code : error.message));
      setStatus('error');
      analytics.track('Email Verification Failed', { error: error.code });
    }
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return "Password must be at least 8 characters long";
    }
    if (!hasUpperCase) {
      return "Password must contain at least one uppercase letter";
    }
    if (!hasLowerCase) {
      return "Password must contain at least one lowercase letter";
    }
    if (!hasNumbers) {
      return "Password must contain at least one number";
    }
    if (!hasSpecialChar) {
      return "Password must contain at least one special character";
    }
    return "";
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    const passwordValidationError = validatePassword(newPassword);
    if (passwordValidationError) {
      setError(passwordValidationError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const actionCode = searchParams.get('oobCode');
    try {
      await confirmPasswordReset(auth, actionCode, newPassword);
      setStatus('success');
      setShowSuccessMessage(true);
      analytics.track('Password Reset Success');
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setError(firebaseErrorHandler(typeof error.code === 'string' ? error.code : error.message));
      analytics.track('Password Reset Failed', { error: error.code });
    }
  };

  const handleContinue = () => {
    navigate('/login');
  };

  const renderContent = () => {
    switch (status) {
      case 'processing':
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h2" 
              sx={{ 
                mb: 4,
                color: 'text.primary',
                fontWeight: 600
              }}
            >
              Processing...
            </Typography>
            <CircularProgress 
              size={isMobile ? 40 : 48} 
              sx={{ 
                color: 'brand.main',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                }
              }} 
            />
          </Box>
        );

      case 'resetPassword':
        return (
          <Box sx={{ width: '100%' }}>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h2" 
              sx={{ 
                mb: 4, 
                textAlign: 'center',
                color: 'text.primary',
                fontWeight: 400
              }}
            >
              Reset Password
            </Typography>
            <Typography
              variant="body2"
              sx={{ mb: 3, textAlign: "center", color: "text.secondary" }}
            >
              Enter your new password below
            </Typography>
            <Box 
              component="form" 
              onSubmit={handlePasswordReset} 
              sx={{ 
                mt: 1,
                width: '100%'
              }}
            >
              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 2,
                    borderRadius: 1
                  }}
                >
                  {error}
                </Alert>
              )}
              <OutlinedInput
                placeholder="New Password"
                value={newPassword}
                type={showPassword ? "text" : "password"}
                sx={{
                  bgcolor: "background.paper",
                  borderRadius: 2,
                  mb: 2,
                  "& .MuiOutlinedInput-input": {
                    py: 1.5,
                    fontSize: "1.1rem",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.divider
                        : theme.palette.grey[300],
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.divider
                        : theme.palette.grey[400],
                  },
                }}
                onChange={(e) => setNewPassword(e.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: "text.secondary" }}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                }
                fullWidth
              />
              <OutlinedInput
                placeholder="Confirm Password"
                value={confirmPassword}
                type={showConfirmPassword ? "text" : "password"}
                sx={{
                  bgcolor: "background.paper",
                  borderRadius: 2,
                  "& .MuiOutlinedInput-input": {
                    py: 1.5,
                    fontSize: "1.1rem",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.divider
                        : theme.palette.grey[300],
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.divider
                        : theme.palette.grey[400],
                  },
                }}
                onChange={(e) => setConfirmPassword(e.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      sx={{ color: "text.secondary" }}
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                }
                fullWidth
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  mb: 2,
                  bgcolor: 'brand.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'brand.dark',
                  },
                  textTransform: 'none',
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 500,
                  borderRadius: 2,
                }}
                disabled={!newPassword || !confirmPassword}
              >
                Reset Password
              </Button>
            </Box>
          </Box>
        );

      case 'success':
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h2" 
              sx={{ 
                mb: 2,
                color: 'brand.main',
                fontWeight: 600
              }}
            >
              {mode === 'verifyEmail' ? 'Email Verified!' : 'Password Reset Successful!'}
            </Typography>
            <Typography 
              color="text.secondary" 
              sx={{ 
                mb: 4,
                px: isMobile ? 2 : 4
              }}
            >
              {mode === 'verifyEmail'
                ? 'Your email has been successfully verified. Redirecting to login...'
                : 'Your password has been successfully reset. Redirecting to login...'}
            </Typography>
            <CircularProgress 
              size={24} 
              sx={{ 
                color: 'brand.main',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                }
              }} 
            />
          </Box>
        );

      case 'error':
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h2" 
              sx={{ 
                mb: 2,
                color: 'error.main',
                fontWeight: 600
              }}
            >
              Action Failed
            </Typography>
            <Typography 
              color="text.secondary" 
              sx={{ 
                mb: 4,
                px: isMobile ? 2 : 4
              }}
            >
              {error}
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={handleContinue}
              sx={{
                bgcolor: 'brand.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'brand.dark',
                },
                textTransform: 'none',
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 500,
                borderRadius: 2,
              }}
            >
              Return to Login
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          px: isMobile ? 2 : 3,
          py: isMobile ? 3 : 4,
        }}
      >
        <Container maxWidth="sm" sx={{ p: 0 }}>
          <Paper
            elevation={3}
            sx={{
              p: isMobile ? 3 : 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              borderRadius: 2,
              bgcolor: 'background.paper',
            }}
          >
            {renderContent()}
          </Paper>
        </Container>
      </Box>
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={3000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        className="notch"
      >
        <Alert
          severity="success"
          sx={{
            width: '100%',
            backgroundColor: 'white',
            boxShadow: 3,
            fontWeight: 600,
            border: '1px solid',
            borderColor: 'brand.main',
          }}
        >
          {mode === 'verifyEmail' 
            ? 'Email verified successfully! Redirecting to login...'
            : 'Password reset successful! Redirecting to login...'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AuthAction;